import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { dataService, MenuItem, Table } from '../../services/dataService';
import { getTenantById } from '../../firebase/config';
import { usePublicMenuFilters } from './hooks/usePublicMenuFilters';
import { readPublicMenuCache, writePublicMenuCache } from './menuCache';
import { TenantData, CustomerInfo, CartItem, MenuViewMode } from './publicMenuTypes';
import PublicMenuHeader from './PublicMenuHeader';
import PublicMenuControls from './PublicMenuControls';
import CompactMenuList from './CompactMenuList';
import ImageMenuGrid from './ImageMenuGrid';
import PublicMenuCustomerModal from './PublicMenuCustomerModal';
import PublicMenuCartModal from './PublicMenuCartModal';
import PublicMenuOrderStatusModal from './PublicMenuOrderStatusModal';

const VIEW_MODE_STORAGE_KEY = 'cafelawn_public_menu_view_mode';

const PublicMenu: React.FC = () => {
  const { tenantId, tableNumber } = useParams<{ tenantId: string; tableNumber?: string }>();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [viewMode, setViewMode] = useState<MenuViewMode>('compact');

  const [tableData, setTableData] = useState<Table | null>(null);
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({ name: '', phone: '' });
  const [isTableMode, setIsTableMode] = useState(false);

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);

  const [currentOrders, setCurrentOrders] = useState<any[]>([]);
  const [showOrderStatus, setShowOrderStatus] = useState(false);

  const { categories, categoryCounts, filteredItems, groupedItems } = usePublicMenuFilters(
    menuItems,
    searchTerm,
    selectedCategory
  );

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (stored === 'compact' || stored === 'images') {
        setViewMode(stored);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const persistViewMode = useCallback((mode: MenuViewMode) => {
    setViewMode(mode);
    try {
      sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (tenantId) {
      void loadMenuData();
      if (tableNumber) {
        setIsTableMode(true);
        void loadTableData();
      } else {
        setIsTableMode(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, tableNumber]);

  useEffect(() => {
    if (!tenantId || !isTableMode || !customerInfo.name) return;

    const unsubscribe = dataService.subscribeToOrders(tenantId, (allOrders) => {
      const tableOrders = allOrders.filter(
        (order) =>
          order.tableNumber === tableNumber &&
          order.customer === customerInfo.name &&
          order.status === 'active'
      );
      setCurrentOrders(tableOrders);
    });

    return () => unsubscribe();
  }, [isTableMode, customerInfo.name, tenantId, tableNumber]);

  const loadMenuData = async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      setError('');

      const tenant = await getTenantById(tenantId);
      if (!tenant) {
        setError('Cafe not found');
        return;
      }

      const tenantInfo = tenant as TenantData;
      if (tenantInfo.isQrOrderingEnabled === false) {
        setError('QR ordering is temporarily unavailable. Please contact the restaurant directly.');
        return;
      }

      setTenantData(tenantInfo);

      const cached = readPublicMenuCache(tenantId);
      if (cached?.length) {
        const availableCached = cached.filter((item) => item.isAvailable);
        if (availableCached.length) {
          setMenuItems(availableCached);
        }
      }

      const items = await dataService.getMenuItems(tenantId);
      const availableItems = items.filter((item) => item.isAvailable);
      setMenuItems(availableItems);
      writePublicMenuCache(tenantId, availableItems);
    } catch (err) {
      console.error('Error loading menu data:', err);
      setError('Unable to load menu. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadTableData = async () => {
    if (!tenantId || !tableNumber) return;
    try {
      const table = await dataService.getTableByNumber(tenantId, tableNumber);
      if (table) {
        setTableData(table);
        const storedCustomer = sessionStorage.getItem(`customer_${tenantId}_${tableNumber}`);
        if (storedCustomer) {
          setCustomerInfo(JSON.parse(storedCustomer) as CustomerInfo);
        } else {
          setShowCustomerInfo(true);
        }
      } else {
        setError(`Table ${tableNumber} not found`);
      }
    } catch (err) {
      console.error('Error loading table data:', err);
      setError('Unable to load table information');
    }
  };

  const handleCustomerInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customerInfo.name.trim()) {
      sessionStorage.setItem(`customer_${tenantId}_${tableNumber}`, JSON.stringify(customerInfo));
      setShowCustomerInfo(false);
    }
  };

  const handleCustomerInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'requested':
        return 'bg-yellow-100 text-yellow-800';
      case 'preparing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrderStatusText = (status: string) => {
    switch (status) {
      case 'requested':
        return 'Order Received';
      case 'preparing':
        return 'Preparing';
      case 'completed':
        return 'Ready';
      default:
        return status;
    }
  };

  const addToCart = (item: MenuItem) => {
    const existingItem = cartItems.find((cartItem) => cartItem.menuItemId === item.id);
    if (existingItem) {
      setCartItems((prev) =>
        prev.map((cartItem) =>
          cartItem.menuItemId === item.id
            ? {
                ...cartItem,
                quantity: cartItem.quantity + 1,
                totalPrice: (cartItem.quantity + 1) * cartItem.unitPrice,
              }
            : cartItem
        )
      );
    } else {
      const newCartItem: CartItem = {
        id: `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        menuItemId: item.id!,
        menuItemName: item.name,
        quantity: 1,
        unitPrice: item.price,
        totalPrice: item.price,
        notes: '',
      };
      setCartItems((prev) => [...prev, newCartItem]);
    }
  };

  const updateCartItemQuantity = (cartItemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      setCartItems((prev) => prev.filter((item) => item.id !== cartItemId));
      return;
    }
    setCartItems((prev) =>
      prev.map((cartItem) =>
        cartItem.id === cartItemId
          ? { ...cartItem, quantity: newQuantity, totalPrice: newQuantity * cartItem.unitPrice }
          : cartItem
      )
    );
  };

  const removeFromCart = (cartItemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== cartItemId));
  };

  const clearCart = () => setCartItems([]);

  const getCartTotal = () => cartItems.reduce((total, item) => total + item.totalPrice, 0);

  const getCartItemCount = () => cartItems.reduce((count, item) => count + item.quantity, 0);

  const getItemQuantityInCart = (menuItemId: string) => {
    const cartItem = cartItems.find((item) => item.menuItemId === menuItemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const handleIncrementFromRow = (cartItemId: string) => {
    const ci = cartItems.find((c) => c.id === cartItemId);
    if (ci) updateCartItemQuantity(ci.id, ci.quantity + 1);
  };

  const handleDecrementFromRow = (cartItemId: string) => {
    const ci = cartItems.find((c) => c.id === cartItemId);
    if (ci) updateCartItemQuantity(ci.id, ci.quantity - 1);
  };

  const placeOrder = async () => {
    if (!isTableMode || !customerInfo.name || cartItems.length === 0) return;
    try {
      setOrderLoading(true);
      const orderItems = cartItems.map((cartItem) => ({
        id: cartItem.id,
        menuItemId: cartItem.menuItemId,
        menuItemName: cartItem.menuItemName,
        quantity: cartItem.quantity,
        unitPrice: cartItem.unitPrice,
        totalPrice: cartItem.totalPrice,
        status: 'requested' as const,
        notes: cartItem.notes || '',
      }));

      const orderData = {
        customer: customerInfo.name,
        customerId: '',
        customerPhone: customerInfo.phone || undefined,
        items: orderItems,
        subtotal: getCartTotal(),
        tax: 0,
        total: getCartTotal(),
        status: 'active' as const,
        isPaid: false,
        paidAmount: 0,
        remainingAmount: getCartTotal(),
        tableNumber,
        notes: `Table ${tableNumber} order via QR`,
      };

      await dataService.createOrder(tenantId!, orderData);
      clearCart();
      setShowCart(false);
      alert(`Order placed successfully! Your order for Table ${tableNumber} has been sent to the kitchen.`);
    } catch (err) {
      console.error('Error placing order:', err);
      alert('Failed to place order. Please try again or contact staff.');
    } finally {
      setOrderLoading(false);
    }
  };

  /** Full-screen loader until tenant is known and we have menu data (or confirmed empty after fetch). */
  const showBlockingLoader = !error && loading && menuItems.length === 0;

  if (showBlockingLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
          <p className="mt-3 text-sm font-medium text-zinc-600">Loading menu…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4">
        <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
          <AlertCircle className="mx-auto mb-3 h-12 w-12 text-red-500" />
          <h1 className="text-xl font-bold text-zinc-900">
            {error.includes('temporarily unavailable') ? 'Temporarily unavailable' : 'Something went wrong'}
          </h1>
          <p className="mt-2 text-sm text-zinc-600">{error}</p>
          <button
            type="button"
            onClick={() => void loadMenuData()}
            className="mt-5 rounded-full bg-amber-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!tenantData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <PublicMenuHeader
        tenantData={tenantData}
        isTableMode={isTableMode}
        tableData={tableData}
        customerName={customerInfo.name}
        cartCount={getCartItemCount()}
        cartTotal={getCartTotal()}
        activeOrderCount={currentOrders.length}
        onOpenCart={() => setShowCart(true)}
        onOpenOrderStatus={() => setShowOrderStatus(true)}
      />

      <PublicMenuControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        categoryCounts={categoryCounts}
        viewMode={viewMode}
        onViewModeChange={persistViewMode}
      />

      <main className="mx-auto max-w-5xl px-3 py-4 tablet:px-4">
        {filteredItems.length === 0 ? (
          <div className="rounded-xl border border-zinc-200 bg-white py-12 text-center shadow-sm">
            <p className="text-base font-semibold text-zinc-900">{searchTerm ? 'No matches' : 'Menu coming soon'}</p>
            <p className="mt-1 px-4 text-sm text-zinc-500">
              {searchTerm ? `Nothing matches “${searchTerm}”.` : 'Check back shortly.'}
            </p>
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="mt-4 rounded-full bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
              >
                Clear search
              </button>
            ) : null}
          </div>
        ) : viewMode === 'compact' ? (
          <CompactMenuList
            groupedItems={groupedItems}
            isTableMode={isTableMode}
            customerName={customerInfo.name}
            cartItems={cartItems}
            getItemQuantityInCart={getItemQuantityInCart}
            onAddToCart={addToCart}
            onIncrementCart={handleIncrementFromRow}
            onDecrementCart={handleDecrementFromRow}
          />
        ) : (
          <ImageMenuGrid
            groupedItems={groupedItems}
            isTableMode={isTableMode}
            customerName={customerInfo.name}
            cartItems={cartItems}
            getItemQuantityInCart={getItemQuantityInCart}
            onAddToCart={addToCart}
            onIncrementCart={handleIncrementFromRow}
            onDecrementCart={handleDecrementFromRow}
          />
        )}
      </main>

      <footer className="mx-auto mt-6 max-w-5xl border-t border-zinc-200 px-3 py-4 text-center text-[11px] text-zinc-500 tablet:px-4">
        <p>Thank you for visiting {tenantData.businessName}</p>
        <p className="mt-0.5">Powered by CafeLawn</p>
      </footer>

      <PublicMenuCustomerModal
        open={showCustomerInfo && isTableMode}
        tableNumber={tableNumber}
        customerInfo={customerInfo}
        onClose={() => setShowCustomerInfo(false)}
        onChange={handleCustomerInfoChange}
        onSubmit={handleCustomerInfoSubmit}
      />

      <PublicMenuOrderStatusModal
        open={showOrderStatus && isTableMode}
        tableNumber={tableNumber}
        orders={currentOrders}
        onClose={() => setShowOrderStatus(false)}
        getOrderStatusColor={getOrderStatusColor}
        getOrderStatusText={getOrderStatusText}
      />

      <PublicMenuCartModal
        open={showCart && isTableMode}
        tableNumber={tableNumber}
        cartItems={cartItems}
        customerName={customerInfo.name}
        orderLoading={orderLoading}
        cartTotal={getCartTotal()}
        onClose={() => setShowCart(false)}
        onUpdateQuantity={updateCartItemQuantity}
        onRemove={removeFromCart}
        onClear={clearCart}
        onPlaceOrder={() => void placeOrder()}
      />
    </div>
  );
};

export default PublicMenu;
